import {
  AppstoreOutlined,
  BankOutlined,
  BookOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  SortAscendingOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Form, Select, Space, Dropdown, Popover } from 'antd';
import { map } from 'lodash';
import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { YEAR_SELECTER } from './helper';
import usePermissionForUser from 'components/hooks/permissions/usePermissionForUser';
import Loading from 'components/Loading';
import { DownloadOutlined } from '@mui/icons-material';
import moment from 'moment-timezone';
import { captureException } from '@sentry/react';
import { saveAs } from "file-saver";
import { formatExportStudentId } from 'utils/formatExportStudentId';

const { Option } = Select;

const isStandardExamReportUserActiveFromApi = (row) => row?.user?.active !== false;

const PROFICIENCY_LEVEL_LEGEND = [
  { range: '0–20%', level: 'Novice' },
  { range: '21–40%', level: 'Early Intermediate' },
  { range: '41–60%', level: 'Intermediate' },
  { range: '61–80%', level: 'Advance' },
  { range: '81–100%', level: 'Advance High' },
];

const ProficiencyLegendContent = () => (
  <LegendPanel>
    <LegendTitle>Proficiency level (overall score)</LegendTitle>
    {PROFICIENCY_LEVEL_LEGEND.map(({ range, level }) => (
      <LegendRow key={level}>
        <LegendRange>{range}</LegendRange>
        <LegendLevel>{level}</LegendLevel>
      </LegendRow>
    ))}
  </LegendPanel>
);

const FilterRecords = ({
  columns,
  data,
  filterRecords,
  updateNestedKey,
  funcObj,
  teacher_map,
  school_option,
  allCourses,
  selectedCourse,
}) => {
  const {
    selected_sort_by,
    selected_class,
    activeSession,
    selected_grade_level,
    selected_student,
    selected_teacher,
  } = filterRecords;
  const { selectedTeacherClasses, isFeedBackShow, optionsData } = data;
  const { handleChangeGradeData, fetchTeacherClasses, fetchStudentData, fetchTeacher, updateStates } = funcObj;
  const { isDistrictAdmin } = usePermissionForUser();

  const filteredClassesByCourse = useMemo(() => {
    if (!isDistrictAdmin || !selectedCourse || !allCourses || allCourses.length === 0) {
      return selectedTeacherClasses;
    }
    const selectedCourseData = allCourses.find(c => c.value === selectedCourse);
    if (!selectedCourseData || !selectedCourseData.classes) {
      return selectedTeacherClasses;
    }
    return selectedCourseData.classes.map(cls => ({
      label: cls.displayName || '',
      value: cls.id
    }));
  }, [selectedCourse, allCourses, selectedTeacherClasses, isDistrictAdmin]);

  const generateFile =async (format = 'csv') => {
    try {
      const uniqueRows = new Set();
      const exportColumns = columns.filter(col => col.key !== 'feedBack');
      const filteredColumns = [
        exportColumns[0],
        { title: 'Student ID', key: 'studentId' },
        ...exportColumns.slice(1),
      ];
      
      (Array.isArray(data?.result) ? data.result : [])
        .filter(isStandardExamReportUserActiveFromApi)
        .map((v) => {
        let userData = {};
        filteredColumns.forEach((acc) => {
          let k_name = acc.title;
          if (acc.key === 'user') {
            userData[k_name] = `${v.user.firstName || ''} ${v.user.lastName || ''}`;
          } else if (acc.key === 'studentId') {
            userData[k_name] = formatExportStudentId(v?.user?.studentId);
          } else if (acc.key === 'overallScore') {
            userData[k_name] = `${v.overallStats?.overallScore || 0}%`;
          } else if (acc.key === 'proficiency_level') {
            userData[k_name] = String(v.overallStats?.proficiency_level ?? '-').replace(/_/g, ' ')
          } else if (acc.key === 'section1') {
            const section1 = v.assignmentData?.[0];
            userData[k_name] = section1?.status === 'Submitted' ? `${section1?.score || 0}%` : '-';
          } else if (acc.key === 'section2') {
            const section2 = v.assignmentData?.[1];
            userData[k_name] = section2?.status === 'Submitted' ? `${section2?.score || 0}%` : '-';
          } else if (acc.key === 'section3') {
            const section3 = v.assignmentData?.[2];
            userData[k_name] = section3?.status === 'Submitted' ? `${section3?.score || 0}%` : '-';
          } else if (acc.key === 'section4') {
            const section4 = v.assignmentData?.[3];
            userData[k_name] = section4?.status === 'Submitted' ? `${section4?.score || 0}%` : '-';
          } else if (acc.key === 'totalCorrect') {
            userData[k_name] = v.overallStats?.totalCorrect || 0;
          } else if (acc.key === 'totalIncorrect') {
            const check = v.assignmentData?.every((item) => item?.status === 'Not Attempted');
            userData[k_name] = check ? '-' : (v.overallStats?.totalIncorrect || 0);
          }
        });
        uniqueRows.add(userData);
      });
      const rowdata = Array.from(uniqueRows);
      const finalData = { data: rowdata };

      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('content_exam');

      const borderThin = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };

      const headerRow = worksheet.addRow(filteredColumns.map((acc) => acc.title));
      headerRow.height = 28;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF02796D' },
        };
        cell.font = {
          color: { argb: 'FFFFFFFF' },
          bold: true,
          size: 11,
        };
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center',
          wrapText: true,
        };
        cell.border = borderThin;
      });

      finalData.data.forEach((userd) => {
        const row = worksheet.addRow(Object.values(userd));
        const excelRowNumber = row.number;
        const dataIdx = excelRowNumber - 2;
        const fillArgb = dataIdx % 2 === 0 ? 'FFFFFFFF' : 'FFF5F5F5';
        row.eachCell((cell) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: fillArgb },
          };
          cell.font = {
            color: { argb: 'FF000000' },
            size: 11,
          };
          cell.alignment = {
            vertical: 'middle',
            horizontal: 'center',
            wrapText: true,
          };
          cell.border = borderThin;
        });
      });


      worksheet.columns.forEach((col) => {
        let maxLength = 10;
        col.eachCell({ includeEmpty: true }, (cell) => {
          const val = cell.value ? cell.value.toString() : '';
          maxLength = Math.max(maxLength, val.length);
        });
        col.width = maxLength + 2;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `content_exam ${moment().format('DD-MM-YY hh:mm:ss')}.xlsx`);
    } catch (error) {
      captureException(`475 Error in generateFile: ${error}`);
      console.error('Error in exam gradebook download:', error);
    }
  };

  const downloadMenu = {
    items: [
      {
        key: 'csv',
        label: 'Download as CSV',
      },
      {
        key: 'xlsx',
        label: 'Download as XLSX',
      },
    ],
    onClick: ({ key }) => {
      generateFile(key);
    },
  };

  return (
    <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
      <FilterContainer>
        <FilterGrid>
          {isDistrictAdmin && !isFeedBackShow?.open ? (
            <FilterItem>
              <FilterLabel>
                <BankOutlined style={{ color: '#6b7280' }} />
                Schools
              </FilterLabel>
              <ModernSelect
                defaultValue={filterRecords?.selected_school}
                style={{ width: '100%' }}
                showSearch
                bordered={false}
                value={filterRecords?.selected_school}
                placeholder={'Select School'}
                options={school_option || []}
                onChange={(v) => {
                  if(isFeedBackShow?.open){
                    updateStates({ isFeedBackShow: { open: false, data: null } });
                  }
                  updateNestedKey(['filterRecords', 'selected_school'], v);
                  fetchTeacher(v);
                }}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </FilterItem>
          ) : null}

          {isDistrictAdmin  && !isFeedBackShow?.open ? (
            <FilterItem>
              <FilterLabel>
                <CalendarOutlined style={{ color: '#6b7280' }} />
                Teacher
              </FilterLabel>
              <ModernSelect
                defaultValue={'all'}
                value={selected_teacher}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
                options={teacher_map}
                onChange={(v) => {
                  updateNestedKey(['filterRecords', 'selected_teacher'], v);
                  fetchTeacherClasses(v);
                }}
                placeholder={'Select Teacher'}
              />
            </FilterItem>
          ) : null}

          {isDistrictAdmin && !isFeedBackShow?.open && allCourses && allCourses.length > 0 ? (
            <FilterItem>
              <FilterLabel>
                <BookOutlined style={{ color: '#6b7280' }} />
                Courses
              </FilterLabel>
              <ModernSelect
                value={selectedCourse}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                options={allCourses.map(c => ({ label: c.label, value: c.value }))}
                onChange={(v) => {
                  updateNestedKey(['selectedCourse'], v);
                  if (v && allCourses) {
                    const selectedCourseData = allCourses.find(c => c.value === v);
                    if (selectedCourseData && selectedCourseData.classes && selectedCourseData.classes.length > 0) {
                      updateNestedKey(['filterRecords', 'selected_class'], selectedCourseData.classes[0].id);
                    } else {
                      updateNestedKey(['filterRecords', 'selected_class'], null);
                    }
                  } else {
                    updateNestedKey(['filterRecords', 'selected_class'], null);
                  }
                }}
                placeholder={'Select Course'}
              />
            </FilterItem>
          ) : null}

          <FilterItem>
            <FilterLabel>
              <TeamOutlined style={{ color: '#6b7280' }} />
              Classes
            </FilterLabel>
            <SortingMenu
              classData={isDistrictAdmin && selectedCourse ? filteredClassesByCourse : selectedTeacherClasses}
              selected_class={selected_class}
              disabled={isFeedBackShow?.open}
              onFilter={(v) => {
                if(isFeedBackShow?.open){
                  updateStates({ isFeedBackShow: { open: false, data: null } });
                }
                updateNestedKey(['filterRecords', 'selected_class'], v);
              }}
            />
          </FilterItem>

          <FilterItem>
            <FilterLabel>
              <CalendarOutlined style={{ color: '#6b7280' }} />
              Session
            </FilterLabel>
            <ModernSelect
              value={activeSession}
              style={{ width: '100%' }}
              onChange={(v) => {
                updateNestedKey(['filterRecords', 'activeSession'], v);
              }}
              placeholder={'Select Session'}
            >
              {map(YEAR_SELECTER, (x) => (
                <Option value={x} key={x}>
                  {x}
                </Option>
              ))}
            </ModernSelect>
          </FilterItem>

          {isFeedBackShow?.open ? null : (
            <FilterItem>
              <FilterLabel>
                <BookOutlined style={{ color: '#6b7280' }} />
                Grade Level
              </FilterLabel>
              <ModernSelect
                value={selected_grade_level}
                onChange={(val) => {
                  updateNestedKey(['filterRecords', 'selected_grade_level'], val);
                  handleChangeGradeData(val);
                }}
                style={{ width: '100%' }}
                placeholder={'Select Grade Level'}
              >
                {optionsData?.standardExam?.map((x) => (
                  <Option value={x.displayName} key={x.id}>
                    {x.displayName}
                  </Option>
                ))}
              </ModernSelect>
            </FilterItem>
          )}

          {isFeedBackShow?.open ? (
            <FilterItem>
              <FilterLabel>
                <BookOutlined style={{ color: '#6b7280' }} />
                Students
              </FilterLabel>
              <ModernSelect
                showSearch
                value={selected_student}
                onChange={async (val) => {
                  const data = await fetchStudentData({ id: val });
                  if (data?.success) {
                    updateNestedKey(['filterRecords', 'selected_student'], val);
                  }
                }}
                filterOption={(input, option) =>
                  (option?.children || '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
                placeholder={'Select Student'}
              >
                {optionsData?.studentList?.map((x) => (
                  <Option value={x.id} key={x.id}>
                    {x.firstName} {x.lastName}
                  </Option>
                ))}
              </ModernSelect>
            </FilterItem>
          ) : null}

          <FilterItem>
            <FilterLabel>
              <AppstoreOutlined style={{ color: '#6b7280' }} />
              Exam
            </FilterLabel>
            <ModernSelect
              value={filterRecords?.selected_exam_book}
              onChange={(v) => {
                updateNestedKey(['filterRecords', 'selected_exam_book'], v);
              }}
              style={{ width: '100%' }}
              placeholder={'Select Exam'}
            >
              {optionsData?.gradeExamOptions?.map((x) => (
                <Option value={x?.bookId} key={x?.id}>
                  {x?.displayName}
                </Option>
              ))}
            </ModernSelect>
          </FilterItem>

          {isFeedBackShow?.open ? null : (
            <FilterItem>
              <FilterLabel>
                <SortAscendingOutlined style={{ color: '#6b7280' }} />
                Sort By
              </FilterLabel>
              <Form.Item label="" name="select" style={{ marginBottom: 0 }}>
                <ModernSelect
                  defaultValue="highest"
                  value={selected_sort_by}
                  style={{ width: '100%' }}
                  onChange={(v) => {
                    updateNestedKey(['filterRecords', 'selected_sort_by'], v);
                  }}
                  placeholder={'Select Sort'}
                >
                  <Option value="highest">Highest</Option>
                  <Option value="lowest">Lowest</Option>
                </ModernSelect>
              </Form.Item>
            </FilterItem>
          )}

          {isFeedBackShow?.open ? null : (
            <ActionButtons>
              {/* {selected_exam_data?.cover && (
                      <div
                          onClick={() => {
                              setImageModalOpen(true);
                          }}
                      >
                          <Image
                              src={selected_exam_data?.cover}
                              className="cursor-pointer"
                              style={{ borderRadius: '8px', border: '2px solid #e5e7eb' }}
                          />
                      </div>
                  )} */}
              <Dropdown menu={downloadMenu} trigger={['click']}>
                <ModernButton
                  style={{ backgroundColor: 'rgb(3, 152, 158)', color: 'white' }}
                >
                  <DownloadOutlined />
                  Download
                </ModernButton>
              </Dropdown>
              <Popover
                content={<ProficiencyLegendContent />}
                trigger="click"
                placement="bottomRight"
                overlayStyle={{ maxWidth: 320 }}
              >
                <InfoIconButton type="button" aria-label="Proficiency level legend">
                  <InfoCircleOutlined />
                </InfoIconButton>
              </Popover>
            </ActionButtons>
          )}
        </FilterGrid>
      </FilterContainer>
    </Space>
  );
};

export default FilterRecords;

const SortingMenu = ({ classData, onFilter, selected_class, disabled = false }) => {
  const [selectedClass, setSelectedClass] = useState(null);

  useEffect(() => {
    setSelectedClass(selected_class);
  }, [selected_class]);

  const onSelectCh = (txt) => {
    setSelectedClass(txt);
    onFilter(txt);
  };
  return (
    <div>
      <ModernSelect
        className="apExamSelect"
        showSearch
        bordered={false}
        placeholder={'Select classes'}
        style={{ width: '100%' }}
        value={selectedClass}
        disabled={disabled}
        onChange={onSelectCh}
        options={classData}
        filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
      />
    </div>
  );
};

const FilterContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  padding-top: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;

  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    margin-bottom: 12px;
  }
`;

const FilterTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 20px;
  align-items: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
`;

const ModernSelect = styled(Select)`
  .ant-select-selector {
    border-radius: 8px !important;
    border: 1px solid #e5e7eb !important;
    background-color: #ffffff !important;
    height: 44px !important;
    padding: 8px 12px !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
    transition: all 0.2s ease !important;

    &:hover {
      border-color: #d1d5db !important;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
    }

    &:focus {
      border-color: #3b82f6 !important;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
    }
  }

  .ant-select-arrow {
    color: #6b7280 !important;
  }

  .ant-select-selection-item {
    color: #1f2937 !important;
    font-weight: 500 !important;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: -10px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const ModernButton = styled.button`
  border-radius: 8px !important;
  border: none !important;
  cursor: pointer !important;
  outline: none !important;
  transition: all 0.2s ease !important;
  height: 44px !important;
  padding: 0 20px !important;
  font-weight: 500 !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;

  &.primary {
    background: #3b82f6 !important;
    color: #ffffff !important;

    &:hover {
      background: #2563eb !important;
      transform: translateY(-1px) !important;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
    }
  }

  &.secondary {
    background: #f3f4f6 !important;
    color: #374151 !important;

    &:hover {
      background: #e5e7eb !important;
      transform: translateY(-1px) !important;
    }
  }
`;

const Image = styled.img`
  width: 60px;
  height: 67px;
`;

const InfoIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  cursor: pointer;
  color: rgb(3, 152, 158);
  font-size: 20px;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgb(3, 152, 158);
    background: #f0fafb;
  }
`;

const LegendPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 240px;
`;

const LegendTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-size: 12px;
  line-height: 1.4;
`;

const LegendRange = styled.span`
  color: #6b7280;
  font-weight: 500;
  white-space: nowrap;
`;

const LegendLevel = styled.span`
  color: #111827;
  font-weight: 600;
  text-align: right;
`;
